import React from 'react';
import { Typography, TypographyProps, Box, Chip } from '@mui/material';
import { VideoLibrary as VideoIcon } from '@mui/icons-material';
import DOMPurify from 'dompurify';

interface RichTextDisplayProps extends Omit<TypographyProps, 'children'> {
  content: string;
  component?: React.ElementType;
  preview?: boolean; // If true, hide videos and show indicator instead
  maxLength?: number; // Maximum length for preview truncation
}

/**
 * SECURITY FIX: Using DOMPurify for HTML sanitization
 * Previous regex-based sanitization was vulnerable to:
 * - HTML entity encoding bypass
 * - Nested tag attacks
 * - Event handler injection if attributes were kept
 *
 * DOMPurify is a battle-tested library maintained by security experts
 * that handles all edge cases and is regularly updated for new attack vectors.
 */
const sanitizeHtml = (html: string): string => {
  if (!html) return '';

  // If it's plain text (no HTML tags), return as-is
  if (!html.includes('<')) {
    return html;
  }

  // Configure DOMPurify with strict settings
  // Only allow basic formatting tags that are safe for display
  const config: DOMPurify.Config = {
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'sup', 'sub', 'br', 'p'],
    ALLOWED_ATTR: [], // No attributes allowed for maximum security
    KEEP_CONTENT: true, // Preserve text content of disallowed tags
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
    SAFE_FOR_TEMPLATES: true, // Extra protection against template injection
    // Additional security: explicitly forbid dangerous tags
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
  };

  // Sanitize with DOMPurify
  return DOMPurify.sanitize(html, config);
};

// Function to detect and convert YouTube URLs to embedded players or indicators
const processYouTubeContent = (text: string, preview = false, maxLength?: number) => {
  // YouTube URL regex patterns
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/g;

  // Split content by YouTube URLs
  const parts = text.split(youtubeRegex);
  const elements: React.ReactNode[] = [];
  let totalTextLength = 0;
  const shouldTruncate = maxLength && text.length > maxLength;
  
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      // Regular text content
      if (parts[i]) {
        let textContent = parts[i];
        
        // Apply truncation if needed and we're in preview mode
        if (preview && shouldTruncate && maxLength) {
          const remainingLength = maxLength - totalTextLength;
          if (remainingLength <= 0) {
            break; // Skip remaining content
          }
          if (textContent.length > remainingLength) {
            textContent = textContent.substring(0, remainingLength) + '...';
          }
          totalTextLength += textContent.length;
        }
        
        elements.push(
          <Typography
            key={i}
            variant="body1"
            component="div"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              mb: preview ? 0 : 2,
              '& p': {
                margin: 0,
                marginBottom: preview ? 0 : '0.5em',
                '&:last-child': {
                  marginBottom: 0
                }
              },
              '& strong, & b': {
                fontWeight: 'bold'
              },
              '& em, & i': {
                fontStyle: 'italic'
              },
              '& u': {
                textDecoration: 'underline'
              }
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(textContent) }}
          />
        );
      }
    } else {
      // YouTube video ID
      const videoId = parts[i];
      if (videoId && videoId.length === 11) {
        if (preview) {
          // In preview mode, always show video indicator regardless of truncation
          elements.push(
            <Box key={i} component="span" sx={{ display: 'inline-block', verticalAlign: 'middle' }}>
              <Chip
                icon={<VideoIcon sx={{ fontSize: '14px !important' }} />}
                label="Video"
                size="small"
                variant="outlined"
                color="primary"
                sx={{ 
                  height: '20px',
                  fontSize: '0.7rem',
                  mr: 0.5,
                  my: 0,
                  display: 'inline-flex',
                  verticalAlign: 'middle',
                  '& .MuiChip-label': {
                    px: 0.5,
                    py: 0,
                    lineHeight: 1.2
                  },
                  '& .MuiChip-icon': {
                    fontSize: '14px',
                    marginLeft: '4px',
                    marginRight: '-2px'
                  }
                }}
              />
            </Box>
          );
        } else {
          // Full mode, show embedded video
          elements.push(
            <Box
              key={i}
              sx={{
                position: 'relative',
                paddingBottom: '56.25%', // 16:9 aspect ratio
                height: 0,
                overflow: 'hidden',
                mb: 2,
                borderRadius: 1,
                bgcolor: 'grey.100'
              }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title={`YouTube video ${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              />
            </Box>
          );
        }
      }
    }
  }
  
  return elements;
};

const RichTextDisplay: React.FC<RichTextDisplayProps> = ({
  content,
  component = 'div',
  preview = false,
  maxLength,
  sx,
  ...typographyProps
}) => {
  // Check if content contains YouTube URLs
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/g;
  const hasYouTube = youtubeRegex.test(content);

  if (hasYouTube) {
    // Process content with YouTube embeds or indicators
    const processedContent = processYouTubeContent(content, preview, maxLength);
    return (
      <Box sx={sx}>
        {processedContent}
      </Box>
    );
  }

  // Fallback to original implementation for non-YouTube content
  const sanitizedContent = sanitizeHtml(content);
  
  // Custom styles for the rich text content
  const richTextStyles = {
    '& p': {
      margin: 0,
      marginBottom: '0.5em',
      '&:last-child': {
        marginBottom: 0
      }
    },
    '& strong, & b': {
      fontWeight: 'bold'
    },
    '& em, & i': {
      fontStyle: 'italic'
    },
    '& u': {
      textDecoration: 'underline'
    },
    '& sup': {
      fontSize: '0.75em',
      verticalAlign: 'super',
      lineHeight: 0
    },
    '& sub': {
      fontSize: '0.75em',
      verticalAlign: 'sub',
      lineHeight: 0
    },
    whiteSpace: 'pre-wrap',
    ...sx
  };
  
  return (
    <Typography
      {...typographyProps}
      component={component}
      sx={richTextStyles}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

export default RichTextDisplay;